// Copyright (c) Mysten Labs, Inc.
// SPDX-License-Identifier: Apache-2.0

import { Heading } from '@mysten/ui';
import { useState } from 'react';
import toast from 'react-hot-toast';

import { CheckpointsTable } from "../checkpoints/CheckpointsTable";
import { CpcEpochsActivityTable } from './CpcEpochsActivityTable';
import { EpochsActivityTable } from './EpochsActivityTable';
import { TransactionsActivityTable } from "./TransactionsActivityTable";
import { useNetwork } from '~/context';
import { PlayPause } from '~/ui/PlayPause';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '~/ui/Tabs';
import { Network } from '~/utils/api/DefaultRpcClient';

const VALID_TABS = ['transactions', 'epochs', 'checkpoints'];

type Props = {
    initialTab?: string | null;
    initialLimit: number;
    disablePagination?: boolean;
};

const AUTO_REFRESH_ID = 'auto-refresh';
const REFETCH_INTERVAL_SECONDS = 10;
const REFETCH_INTERVAL = REFETCH_INTERVAL_SECONDS * 1000;

export function Activity({ initialTab, initialLimit, disablePagination }: Props) {
    const pollingTxnTableEnabled = true;
    const [network] = useNetwork();
    const isLocal = network === Network.LOCAL;

    const [paused, setPaused] = useState(false);
    const [activeTab, setActiveTab] = useState(() =>
        initialTab && VALID_TABS.includes(initialTab) ? initialTab : 'transactions',
    );

    const handlePauseChange = () => {
        if (paused) {
            toast.success(`Auto-refreshing on - every ${REFETCH_INTERVAL_SECONDS} seconds`, {
                id: AUTO_REFRESH_ID,
            });
        } else {
            toast.success('Auto-refresh paused', { id: AUTO_REFRESH_ID });
        }

        setPaused((paused) => !paused);
    };

    const refetchInterval = paused || !pollingTxnTableEnabled ? undefined : REFETCH_INTERVAL;

    // 获取 Tab 显示文本的辅助函数 (兼顾 H5 缩短文本防止爆掉)
    const getTabLabel = (tab: string, isMobile: boolean) => {
        if (tab === 'transactions') {
            return isMobile ? 'TX Blocks' : 'Transaction Blocks';
        }
        return tab.charAt(0).toUpperCase() + tab.slice(1);
    };

    return (
        <div className="bg-[#0F172A] rounded-lg border border-white/20 shadow-xl overflow-hidden">
            <Tabs size="lg" value={activeTab} onValueChange={setActiveTab}>
                <div className="bg-white/10 p-4 md:p-6 flex items-center justify-between gap-4 border-b border-white/5">
                    <div className="overflow-x-auto [scrollbar-width:none] [&::-webkit-scrollbar]:hidden flex-1">
                        <TabsList className="bg-transparent border-none flex justify-start gap-5 md:gap-8 whitespace-nowrap">
                            {VALID_TABS.map((tab) => (
                                <TabsTrigger
                                    key={tab}
                                    value={tab}
                                    className="!bg-transparent px-0 py-1.5 md:py-2 border-b-2 border-transparent data-[state=active]:border-[#38BDF8] data-[state=active]:text-[#38BDF8] transition-colors"
                                >
                                    <Heading variant="heading4/semibold">
                                        {/* PC 端 */}
                                        <span className="hidden sm:inline">{getTabLabel(tab, false)}</span>
                                        {/* H5 端 */}
                                        <span className="inline sm:hidden">{getTabLabel(tab, true)}</span>
                                    </Heading>
                                </TabsTrigger>
                            ))}
                        </TabsList>
                    </div>
                    {pollingTxnTableEnabled && activeTab === 'transactions' && (
                        <div className="flex items-center shrink-0 text-xl md:text-2xl">
                            <PlayPause paused={paused} onChange={handlePauseChange} />
                        </div>
                    )}
                </div>
                <TabsContent value="transactions" className="outline-none">
                    <TransactionsActivityTable
                        refetchInterval={refetchInterval}
                        initialLimit={initialLimit}
                        disablePagination={disablePagination}
                        transactionKindFilter={undefined}
                    />
                </TabsContent>
                <TabsContent value="epochs" className="outline-none">
                    {isLocal ? (
                        <CpcEpochsActivityTable
                            refetchInterval={refetchInterval}
                            initialLimit={initialLimit}
                            disablePagination={disablePagination}
                        />
                    ) : (
                        <EpochsActivityTable
                            refetchInterval={refetchInterval}
                            initialLimit={initialLimit}
                            disablePagination={disablePagination}
                        />
                    )}
                </TabsContent>
                <TabsContent value="checkpoints" className="outline-none">
                    <CheckpointsTable
                        refetchInterval={refetchInterval}
                        initialLimit={initialLimit}
                        disablePagination={disablePagination}
                    />
                </TabsContent>

            </Tabs>
        </div>
    );
}